import { useState } from "react";
import { Button, Card, CardBody, Divider } from "@heroui/react";
import { IconCalendar, IconTag } from "@tabler/icons-react";
import toast from "react-hot-toast";
import { useTranslations, useLocale } from "../../../../../i18n";
import useBookingStore, { PaymentMethods } from "@/store/booking-store";
import { useAuthStore } from "@/store/auth-store";
import { useStudioStore } from "@/store/studio-store";
import { useViewStore } from "@/store/view-store";
import { api } from "@/lib/api";
import { formatDateWithLocale } from "@/utils/format-date-with-locale";
import PaymentMethodsSelection from "./payment-methods";
import EmbeddedStripeCheckout from "./EmbeddedStripeCheckout";
import TwintQrPayment from "./twint-qr-payment";

type EventPricingOption = {
  id: string;
  title: string;
  description?: string | null;
  price: number | null;
  currency?: string | null;
};

function EventPricingSelection({
  options,
  selected,
  onSelect,
  currency,
}: {
  options: EventPricingOption[];
  selected: EventPricingOption | null;
  onSelect: (o: EventPricingOption) => void;
  currency: string;
}) {
  const t = useTranslations("checkout");

  if (!options.length) {
    return (
      <Card shadow="none" className="border border-dashed">
        <CardBody className="py-8 text-center text-gray-400 text-sm">
          {t("noBookingOptions")}
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold text-gray-700">
        {t("selectPricingOption")}
      </h2>
      {options.map((option) => {
        const isSelected = selected?.id === option.id;
        return (
          <Card
            key={option.id}
            shadow="none"
            isPressable
            onPress={() => onSelect(option)}
            className={`border-2 transition-colors cursor-pointer ${
              isSelected
                ? "border-primary bg-primary-50"
                : "border-gray-200 hover:border-primary-200"
            }`}
          >
            <CardBody className="flex flex-row items-center justify-between p-4">
              <div className="flex-1">
                <p className="font-semibold text-gray-800">{option.title}</p>
                {option.description && (
                  <p className="text-sm text-gray-500 mt-0.5">
                    {option.description}
                  </p>
                )}
              </div>
              <div className="text-right ml-4">
                <p className="text-lg font-bold text-primary">
                  {Number(option.price ?? 0).toFixed(2)}{" "}
                  {option.currency ?? currency}
                </p>
              </div>
            </CardBody>
          </Card>
        );
      })}
    </div>
  );
}

function EventCheckoutSummary({
  selectedPricing,
  currency,
  isSubmitting,
  isValid,
  onSubmit,
}: {
  selectedPricing: EventPricingOption | null;
  currency: string;
  isSubmitting: boolean;
  isValid: boolean;
  onSubmit: () => void;
}) {
  const t = useTranslations("checkout");
  const locale = useLocale();
  const { selectedEvent } = useBookingStore();

  const total = selectedPricing ? Number(selectedPricing.price ?? 0) : null;
  const pricingCurrency = selectedPricing?.currency ?? currency;

  return (
    <div className="col-span-1">
      <Card className="p-6 sticky top-6" shadow="sm">
        <h2 className="text-lg text-gray-500 font-semibold mb-4">
          {t("summary")}
        </h2>

        <div className="space-y-4">
          {selectedEvent && (
            <div>
              <h3 className="text-xl font-semibold">{selectedEvent.title}</h3>
              <div className="flex items-center gap-2 text-gray-500 mt-1">
                <IconCalendar size={16} />
                <span className="text-sm">
                  {selectedEvent.startAt &&
                    formatDateWithLocale(
                      new Date(selectedEvent.startAt as Date),
                      "dd MMMM yyyy",
                      locale,
                    )}
                  {selectedEvent.endAt &&
                    selectedEvent.startAt !== selectedEvent.endAt && (
                      <>
                        {" — "}
                        {formatDateWithLocale(
                          new Date(selectedEvent.endAt as Date),
                          (selectedEvent as any).type === "RETREAT"
                            ? "dd MMMM yyyy"
                            : "HH:mm",
                          locale,
                        )}
                      </>
                    )}
                </span>
              </div>
            </div>
          )}

          <Divider />

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-gray-600">
              <IconTag size={16} />
              <span className="text-sm">
                {selectedPricing
                  ? selectedPricing.title
                  : t("selectBookingMethod")}
              </span>
            </div>
            {selectedPricing && (
              <span className="font-medium">
                {total?.toFixed(2)} {pricingCurrency}
              </span>
            )}
          </div>

          {selectedPricing && (
            <>
              <Divider />
              <div className="flex justify-between items-center">
                <span className="font-medium">{t("total")}</span>
                <span className="text-xl font-bold">
                  {total?.toFixed(2)} {pricingCurrency}
                </span>
              </div>
              <div className="text-sm text-gray-400 text-right">
                {t("inclVat")}
              </div>
            </>
          )}

          <Button
            color="primary"
            size="lg"
            className="w-full"
            onPress={onSubmit}
            isLoading={isSubmitting}
            isDisabled={!isValid}
          >
            {t("bookNow")}
          </Button>
        </div>
      </Card>
    </div>
  );
}

export function EventBooking() {
  const t = useTranslations("checkout");
  const { selectedEvent } = useBookingStore();
  const { paymentMethod } = useBookingStore();
  const { user } = useAuthStore();
  const studio = useStudioStore((state) => state.studio);
  const { onBookingSuccess } = useViewStore();

  const [selectedPricing, setSelectedPricing] =
    useState<EventPricingOption | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [bookingId, setBookingId] = useState<string | null>(null);
  const [twintPendingBookingId, setTwintPendingBookingId] = useState<
    string | null
  >(null);

  if (!selectedEvent) return null;

  const currency = studio?.currency ?? "CHF";
  const eventPricingOptions: EventPricingOption[] =
    (selectedEvent.pricing as any)?.eventPricing ?? [];

  const requiresPayment = selectedPricing
    ? Number(selectedPricing.price ?? 0) > 0
    : false;
  const isValid =
    selectedPricing !== null && (!requiresPayment || !!paymentMethod);

  const handleSubmit = async () => {
    if (!user || !studio || !selectedPricing) return;

    if (requiresPayment && !paymentMethod) {
      toast.error(t("selectPaymentMethodFirst"));
      return;
    }

    try {
      setIsSubmitting(true);

      const payload = {
        eventId: selectedEvent.id,
        bookingMethod: "DROP_IN",
        paymentMethod: requiresPayment ? paymentMethod : "CASH",
        organizationId: studio.id,
        pricingOptionId: selectedPricing.id,
        participants: [],
      };

      const data = await api("/api/widget/bookings/create-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!data.requiresPayment && paymentMethod === PaymentMethods.TWINT) {
        setTwintPendingBookingId(data.bookingId);
        return;
      }

      if (!data.requiresPayment) {
        toast.success(t("bookingSuccessful"));
        onBookingSuccess(data.bookingId);
        return;
      }

      if (data.clientSecret) {
        setClientSecret(data.clientSecret);
        setBookingId(data.bookingId);
        return;
      }
    } catch (error: any) {
      toast.error(error.message || t("somethingWentWrong"));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (twintPendingBookingId) {
    return (
      <TwintQrPayment
        onComplete={() => {
          toast.success(t("bookingSuccessful"));
          onBookingSuccess(twintPendingBookingId);
          setTwintPendingBookingId(null);
        }}
      />
    );
  }

  if (clientSecret) {
    return (
      <div className="w-full flex flex-col justify-center items-center">
        <Button
          variant="light"
          color="primary"
          onPress={() => setClientSecret(null)}
          className="mb-4 self-start"
          startContent={<span aria-hidden="true">←</span>}
        >
          {t("cancel-payment") ?? "Cancel payment"}
        </Button>
        <EmbeddedStripeCheckout
          clientSecret={clientSecret}
          onComplete={() => {
            onBookingSuccess(bookingId as string);
            setClientSecret(null);
          }}
        />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-8 mt-3 md:mt-8">
      <div className="col-span-1 md:col-span-2 flex flex-col gap-5">
        <Card shadow="none" className="border border-gray-200">
          <CardBody className="p-4">
            <h2 className="text-xl font-semibold text-gray-800">
              {selectedEvent.title}
            </h2>
            {(selectedEvent as any).description && (
              <p className="text-sm text-gray-500 mt-1">
                {(selectedEvent as any).description}
              </p>
            )}
          </CardBody>
        </Card>

        <EventPricingSelection
          options={eventPricingOptions}
          selected={selectedPricing}
          onSelect={setSelectedPricing}
          currency={currency}
        />

        {requiresPayment && <PaymentMethodsSelection />}
      </div>

      <EventCheckoutSummary
        selectedPricing={selectedPricing}
        currency={currency}
        isSubmitting={isSubmitting}
        isValid={isValid}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
